import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  calculateExtensionDate,
  downloadReaffirmationExtensionPdf,
  formatLongDate,
  prepareReaffirmationExtensionPdfs,
} from "./reaffirmationExtensionGenerator";
import { filingDebtors as getFilingDebtors } from "../utils/debtors";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

async function apiJson(path, options = {}) {
  const token = localStorage.getItem("accessToken");
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.detail || payload.error || `Request failed (${response.status})`);
  }
  return payload;
}

function debtorName(debtor) {
  return [
    debtor?.first_name,
    debtor?.second_name || debtor?.middle_name,
    debtor?.last_name,
    debtor?.suffix,
  ].filter(Boolean).join(" ");
}

export default function ReaffirmationExtension() {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [daysExtended, setDaysExtended] = useState("");
  const [loadingCase, setLoadingCase] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preparedPdfs, setPreparedPdfs] = useState(null);
  const [filingPreflight, setFilingPreflight] = useState(null);
  const [filing, setFiling] = useState(null);
  const [filingError, setFilingError] = useState("");
  const [filingAcknowledged, setFilingAcknowledged] = useState(false);
  const [redactionAcknowledged, setRedactionAcknowledged] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [resolvingDocketNumber, setResolvingDocketNumber] = useState("");
  const [resolvingOrderId, setResolvingOrderId] = useState("");
  const [filingWorking, setFilingWorking] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadCase() {
      setLoadingCase(true);
      setError("");
      try {
        const token = localStorage.getItem("accessToken");
        const headers = { Authorization: `Bearer ${token}` };
        const [response, filingResponse] = await Promise.all([
          fetch(`${API_BASE}/case/${caseId}/overview`, { headers }),
          fetch(`${API_BASE}/firm/cases/${caseId}/cmecf-reaffirmation-extension/preflight/`, { headers }),
        ]);
        if (!response.ok) {
          throw new Error("The case information could not be loaded.");
        }
        const data = await response.json();
        const preflight = filingResponse.ok ? await filingResponse.json() : null;
        if (!cancelled) {
          setCaseData(data);
          setFilingPreflight(preflight);
          const active = (preflight?.recent_filings || []).find((item) =>
            ["queued", "in_progress", "review_required"].includes(item.status)
          );
          setFiling(active || null);
          if (!filingResponse.ok) {
            const payload = await filingResponse.json().catch(() => ({}));
            setFilingError(
              payload.detail || "The CM/ECF reaffirmation-extension review could not be loaded.",
            );
          }
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || "The case information could not be loaded.");
      } finally {
        if (!cancelled) setLoadingCase(false);
      }
    }

    if (caseId) loadCase();
    return () => { cancelled = true; };
  }, [caseId]);

  const filingDebtors = useMemo(() => {
    return getFilingDebtors(caseData?.debtors);
  }, [caseData]);

  const latestFiling = (filingPreflight?.recent_filings || [])[0] || null;
  const resumableFiling = latestFiling?.can_resume ? latestFiling : null;
  const filingActive = filing && ["queued", "in_progress", "review_required"].includes(filing.status);
  const displayedFiling = filing || latestFiling;

  useEffect(() => {
    if (!filing?.id || !["queued", "in_progress"].includes(filing.status)) return undefined;
    let cancelled = false;
    let polling = false;
    const poll = async () => {
      if (cancelled || polling) return;
      polling = true;
      try {
        const current = await apiJson(`/firm/cmecf-reaffirmation-extensions/${filing.id}/`);
        if (!cancelled) {
          setFiling(current);
          setFilingPreflight((previous) => previous ? {
            ...previous,
            recent_filings: [
              current,
              ...(previous.recent_filings || []).filter((item) => item.id !== current.id),
            ],
          } : previous);
        }
      } catch (requestError) {
        if (!cancelled) {
          setFilingError(requestError.message || "Could not check the filing status.");
        }
      } finally {
        polling = false;
      }
    };
    poll();
    const timer = window.setInterval(poll, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [filing?.id, filing?.status]);

  const requestedDays = Number(daysExtended);
  const validDays = Number.isInteger(requestedDays) && requestedDays > 0;
  const extensionDate = useMemo(
    () => calculateExtensionDate(caseData?.reaffirmation_deadline, requestedDays),
    [caseData?.reaffirmation_deadline, requestedDays],
  );
  const isChapterSeven = String(caseData?.chapter || "") === "7";
  const canGenerate = Boolean(
    caseData
    && isChapterSeven
    && caseData.reaffirmation_deadline
    && validDays
    && !generating,
  );

  async function handleGenerate() {
    if (!canGenerate) return;
    setGenerating(true);
    setError("");
    setSuccess("");
    setPreparedPdfs(null);
    try {
      const pdfs = await prepareReaffirmationExtensionPdfs({
        caseData,
        daysExtended: requestedDays,
      });
      setPreparedPdfs(pdfs);
      setSuccess("Both PDFs are ready. Download the motion and proposed order below.");
    } catch (generationError) {
      console.error(generationError);
      setError(generationError.message || "The PDFs could not be generated.");
    } finally {
      setGenerating(false);
    }
  }

  const canFile = Boolean(
    (resumableFiling || canGenerate)
    && filingPreflight?.ready
    && !filingActive
    && filingAcknowledged
    && redactionAcknowledged
    && confirmationText.trim().toUpperCase() === "FILE"
  );

  function updateRecentFiling(current) {
    setFiling(current);
    setFilingPreflight((previous) => previous ? {
      ...previous,
      recent_filings: [
        current,
        ...(previous.recent_filings || []).filter((item) => item.id !== current.id),
      ],
    } : previous);
  }

  async function handleFile() {
    if (!canFile) return;
    setFilingWorking(true);
    setFilingError("");
    try {
      const formData = new FormData();
      formData.append("filing_acknowledged", "true");
      formData.append("redaction_acknowledged", "true");
      formData.append("confirmation_text", "FILE");
      if (resumableFiling) {
        formData.append("resume_filing_id", resumableFiling.id);
      } else {
        const pdfs = await prepareReaffirmationExtensionPdfs({
          caseData,
          daysExtended: requestedDays,
        });
        setPreparedPdfs(pdfs);
        formData.append("days_requested", String(requestedDays));
        formData.append(
          "motion_pdf",
          new Blob([pdfs.motion.bytes], { type: "application/pdf" }),
          pdfs.motion.filename,
        );
        formData.append(
          "order_pdf",
          new Blob([pdfs.order.bytes], { type: "application/pdf" }),
          pdfs.order.filename,
        );
      }
      const current = await apiJson(
        `/firm/cases/${caseId}/cmecf-reaffirmation-extension/start/`,
        { method: "POST", body: formData },
      );
      updateRecentFiling(current);
      setFilingAcknowledged(false);
      setRedactionAcknowledged(false);
      setConfirmationText("");
    } catch (requestError) {
      setFilingError(
        requestError.message || "The reaffirmation-extension filing could not be started.",
      );
    } finally {
      setFilingWorking(false);
    }
  }

  async function resolveUncertain(outcome) {
    if (!filing?.id) return;
    setFilingWorking(true);
    setFilingError("");
    try {
      const current = await apiJson(
        `/firm/cmecf-reaffirmation-extensions/${filing.id}/resolve/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            outcome,
            motion_docket_number: resolvingDocketNumber,
            order_id: resolvingOrderId,
          }),
        },
      );
      updateRecentFiling(current);
    } catch (requestError) {
      setFilingError(requestError.message || "The court-check result could not be saved.");
    } finally {
      setFilingWorking(false);
    }
  }

  async function cancelQueued() {
    if (!filing?.id) return;
    setFilingWorking(true);
    setFilingError("");
    try {
      const current = await apiJson(
        `/firm/cmecf-reaffirmation-extensions/${filing.id}/cancel-queued/`,
        { method: "POST" },
      );
      updateRecentFiling(current);
    } catch (requestError) {
      setFilingError(requestError.message || "The queued request could not be cancelled.");
    } finally {
      setFilingWorking(false);
    }
  }

  return (
    <section className="mx-auto mt-12 max-w-3xl space-y-8">
      <header className="rounded-md bg-gray-100 p-4">
        <h2 className="text-2xl font-semibold">Reaffirmation Extension</h2>
      </header>

      <p className="text-gray-600">
        Choose the number of additional days requested. The motion and proposed order will be
        filled with the case information and prepared as two separate PDFs.
      </p>

      <div className="space-y-6 rounded-lg bg-white p-6 shadow-md">
        {loadingCase && (
          <div className="rounded-md bg-blue-50 p-3 text-blue-800">Loading case information…</div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-red-800">{error}</div>
        )}
        {success && (
          <div className="rounded-md bg-green-50 p-3 text-green-800">{success}</div>
        )}

        {caseData && (
          <>
            <dl className="grid grid-cols-1 gap-4 rounded-md bg-gray-50 p-4 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-gray-500">Case</dt>
                <dd className="font-medium text-gray-900">{caseData.case_number || "Not assigned"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Debtor(s)</dt>
                <dd className="font-medium text-gray-900">
                  {filingDebtors.map(debtorName).filter(Boolean).join(" & ") || "Not available"}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Chapter</dt>
                <dd className="font-medium text-gray-900">{caseData.chapter || "Not available"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Current reaffirmation deadline</dt>
                <dd className="font-medium text-gray-900">
                  {formatLongDate(caseData.reaffirmation_deadline) || "Not available"}
                </dd>
              </div>
            </dl>

            {!isChapterSeven && (
              <div className="rounded-md bg-amber-50 p-3 text-amber-800">
                Reaffirmation extension documents are available for Chapter 7 cases.
              </div>
            )}
            {!caseData.reaffirmation_deadline && (
              <div className="rounded-md bg-amber-50 p-3 text-amber-800">
                Add a reaffirmation deadline to this case before generating the documents.
              </div>
            )}

            <label className="block max-w-sm">
              <span className="mb-1 block text-sm font-medium text-gray-700">
                Number of additional days requested
              </span>
              <input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 disabled:bg-gray-100"
                value={daysExtended}
                onChange={(event) => {
                  setDaysExtended(event.target.value);
                  setSuccess("");
                  setPreparedPdfs(null);
                  setFilingAcknowledged(false);
                  setRedactionAcknowledged(false);
                  setConfirmationText("");
                }}
                placeholder="For example, 30"
                disabled={!isChapterSeven || !caseData.reaffirmation_deadline}
              />
            </label>

            {validDays && extensionDate && (
              <div className="rounded-md border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-900">
                <span className="font-medium">Requested new deadline:</span>{" "}
                {formatLongDate(extensionDate)}
                <p className="mt-1 text-xs text-indigo-700">
                  If the calculated date falls on a weekend, it is moved to the following Monday.
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {generating ? "Preparing PDFs…" : "Prepare Motion and Order PDFs"}
            </button>

            {preparedPdfs && (
              <div className="grid grid-cols-1 gap-4 border-t border-gray-200 pt-6 sm:grid-cols-2">
                <div className="rounded-md border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900">Motion PDF</h3>
                  <p className="mb-4 mt-1 text-sm text-gray-600">
                    Motion to extend the reaffirmation agreement deadline.
                  </p>
                  <button
                    type="button"
                    onClick={() => downloadReaffirmationExtensionPdf(preparedPdfs.motion)}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                  >
                    Download Motion PDF
                  </button>
                </div>

                <div className="rounded-md border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900">Proposed Order PDF</h3>
                  <p className="mb-4 mt-1 text-sm text-gray-600">
                    Proposed order granting the requested extension.
                  </p>
                  <button
                    type="button"
                    onClick={() => downloadReaffirmationExtensionPdf(preparedPdfs.order)}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                  >
                    Download Proposed Order PDF
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="space-y-5 rounded-lg border border-red-200 bg-white p-6 shadow-md">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">File automatically with CM/ECF</h3>
          <p className="mt-1 text-sm text-gray-600">
            This files the motion for {filingDebtors.length > 1 ? "both filing debtors" : "the filing debtor"},
            captures the new docket number, and uploads the proposed order through Single Order
            Upload as “Ex Parte (no Hearing Required).”
          </p>
        </div>

        {filingError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800">
            {filingError}
          </div>
        )}

        {(filingPreflight?.reasons || []).length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-semibold">Automatic filing is not ready:</div>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {filingPreflight.reasons.map((reason) => <li key={reason}>{reason}</li>)}
            </ul>
          </div>
        )}

        {resumableFiling && !filingActive && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
            <div className="font-semibold">Resume the reviewed reaffirmation-extension filing</div>
            <p className="mt-1">
              The saved PDFs will be reused. A completed motion will not be filed twice; only the
              unfinished court step will run.
            </p>
          </div>
        )}

        {!filingActive && (
          <div className="space-y-3">
            <label className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-950">
              <input
                type="checkbox"
                checked={filingAcknowledged}
                onChange={(event) => setFilingAcknowledged(event.target.checked)}
                className="mt-1"
              />
              I reviewed the requested extension, filing debtors, motion, and proposed order. I
              understand this will make a live CM/ECF filing and a live Single Order Upload.
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={redactionAcknowledged}
                onChange={(event) => setRedactionAcknowledged(event.target.checked)}
                className="mt-1"
              />
              I confirm that both PDFs comply with the court&apos;s redaction requirements.
            </label>
            <label className="block text-sm font-medium text-gray-900">
              Type <strong>FILE</strong> to enable the filing button.
              <input
                value={confirmationText}
                onChange={(event) => setConfirmationText(event.target.value)}
                className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 p-2"
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              onClick={handleFile}
              disabled={!canFile || filingWorking}
              className="rounded-md bg-red-700 px-5 py-3 font-bold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {filingWorking
                ? "Preparing…"
                : resumableFiling
                  ? "Resume reaffirmation-extension filing"
                  : "Generate PDFs and file reaffirmation extension"}
            </button>
          </div>
        )}

        {displayedFiling && (
          <div className={`rounded-lg border p-4 ${
            displayedFiling.status === "succeeded"
              ? "border-green-200 bg-green-50"
              : displayedFiling.status === "review_required"
                ? "border-red-300 bg-red-50"
                : ["failed", "partial"].includes(displayedFiling.status)
                  ? "border-amber-300 bg-amber-50"
                  : "border-indigo-200 bg-indigo-50"
          }`}>
            <div className="font-bold text-gray-900">{displayedFiling.status_display}</div>
            <p className="mt-1 text-sm text-gray-700">{displayedFiling.status_message}</p>
            <p className="mt-1 text-sm text-gray-700">
              Requested extension: <strong>{displayedFiling.days_requested} days</strong>
            </p>
            {(displayedFiling.debtor_names || []).length > 0 && (
              <p className="mt-1 text-sm text-gray-700">
                Filing debtor(s): <strong>{displayedFiling.debtor_names.join(" & ")}</strong>
              </p>
            )}
            {displayedFiling.motion_docket_number && (
              <p className="mt-1 text-sm font-semibold text-gray-900">
                Motion docket: {displayedFiling.motion_docket_number}
              </p>
            )}
            {displayedFiling.order_id && (
              <p className="mt-1 text-sm font-semibold text-gray-900">
                Single Order Upload ID: {displayedFiling.order_id}
              </p>
            )}
            {displayedFiling.error_message && (
              <p className="mt-2 text-sm font-medium text-red-800">
                {displayedFiling.error_message}
              </p>
            )}
            <div className="mt-3 space-y-2">
              {(displayedFiling.steps || []).map((step) => (
                <div
                  key={step.key}
                  className="flex flex-wrap justify-between gap-2 rounded-md bg-white/80 px-3 py-2 text-sm"
                >
                  <span className="font-medium text-gray-800">{step.label}</span>
                  <span className={
                    step.status === "succeeded"
                      ? "font-semibold text-green-700"
                      : step.status === "review_required"
                        ? "font-semibold text-red-700"
                        : "text-gray-600"
                  }>
                    {String(step.status || "pending").replaceAll("_", " ")}
                    {step.docket_number ? ` · docket ${step.docket_number}` : ""}
                    {step.order_id ? ` · Order ID ${step.order_id}` : ""}
                  </span>
                </div>
              ))}
            </div>

            {displayedFiling.status === "review_required" && (
              <div className="mt-4 rounded-md border border-red-300 bg-white p-4">
                <p className="text-sm font-bold text-red-900">
                  Check the live docket or Single Order Upload before answering. Do not retry first.
                </p>
                {displayedFiling.current_step === "motion" && (
                  <label className="mt-3 block text-sm text-gray-700">
                    Reaffirmation-extension motion docket number
                    <input
                      value={resolvingDocketNumber}
                      onChange={(event) => setResolvingDocketNumber(event.target.value)}
                      className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 p-2"
                    />
                  </label>
                )}
                {displayedFiling.current_step === "order_upload" && (
                  <label className="mt-3 block text-sm text-gray-700">
                    Order ID, if shown
                    <input
                      value={resolvingOrderId}
                      onChange={(event) => setResolvingOrderId(event.target.value)}
                      className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 p-2"
                    />
                  </label>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => resolveUncertain("completed")}
                    disabled={filingWorking}
                    className="rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    I checked — this step completed
                  </button>
                  <button
                    type="button"
                    onClick={() => resolveUncertain("not_completed")}
                    disabled={filingWorking}
                    className="rounded-md border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-50"
                  >
                    I checked — it did not complete
                  </button>
                </div>
              </div>
            )}

            {displayedFiling.status === "queued" && displayedFiling.can_cancel_queued && (
              <button
                type="button"
                onClick={cancelQueued}
                disabled={filingWorking}
                className="mt-4 rounded-md border border-amber-400 bg-white px-4 py-2 text-sm font-semibold text-amber-900"
              >
                Cancel stalled queue request
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
